import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import VoucherStatus from '../components/VoucherStatus';
import {
    Satellite,
    SatelliteDish,
    ArrowLeftRight
} from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import Slideshow from './Slideshow';
import pic1 from '../assets/slideshow/pic1.jpg';
import pic2 from '../assets/slideshow/pic2.jpg';
import pic3 from '../assets/slideshow/pic3.jpg';
import pic4 from '../assets/slideshow/pic4.jpg';
import pic5 from '../assets/slideshow/pic5.jpg';
import pic6 from '../assets/slideshow/pic6.jpg';
import pic7 from '../assets/slideshow/pic7.jpg';
import pic8 from '../assets/slideshow/pic8.jpg';
import vid1 from '../assets/slideshow/vid1.mp4';
import vid2 from '../assets/slideshow/vid2.mp4';
import AccountModal from './AccountModal';

const Dashboard = () => {
    const navigate = useNavigate();
    const [topics, setTopics] = useState([]);
    const [showTimelines, setShowTimelines] = useState(false);
    const [voucherData, setVoucherData] = useState(null);
    const [error] = useState(false);
    const [showSecurity, setShowSecurity] = useState(false);
    const [showDetectiveLink, setShowDetectiveLink] = useState(false);
    const [showSDSS, setShowSDSS] = useState(false);
    const [showFuelModal, setShowFuelModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [showCommsModal, setShowCommsModal] = useState(false);
    const [showISAJAOModal, setShowISAJAOModal] = useState(false);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);

    // Slideshow items: 8 images, 2 videos in preferred order
    const slideshowItems = [
        { type: 'image', src: pic1 },
        { type: 'image', src: vid1 },
        { type: 'image', src: pic2 },
        { type: 'video', src: pic3 },
        { type: 'image', src: vid2 },
        { type: 'image', src: pic4 },
        { type: 'image', src: pic5 },
        { type: 'video', src: vid1 },
        { type: 'image', src: pic6 },
        { type: 'image', src: pic7 },
        { type: 'image', src: pic8 },
    ];

    useEffect(() => {
        const token = localStorage.getItem('uma_token');
        api.get('/voucher/status', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => setVoucherData(res.data))
            .catch(err => {
                console.error('❌ Failed to fetch voucher status:', err);
                setVoucherData(null);
            });
    }, []);

    useEffect(() => {
        api.get('/topics')
            .then(res => setTopics(res.data))
            .catch(err => console.error('Error fetching topics:', err));
    }, []);

    const subsystems = [
        { path: '/builder', label: 'PROFILE BUILDER', desc: '<-- New users: begin memory file here.' },
        { path: '/profile', label: 'REVIEW / EDIT PROFILE', desc: 'Review or update your UMA Profile.' },
        { path: '/uploads', label: 'UPLOAD CHAT(S)', desc: 'Import past data for UMA injection.' },
        { path: '/chats', label: 'CHAT HISTORY', desc: 'View and manage chat archives.' },
        { path: '/topics', label: 'MANAGE TOPICS', desc: 'Re-align your category matrix.' },
        { path: '/alignment', label: 'ALIGNMENT SETTINGS', desc: 'Configure AI ethical behavior.' },
        { path: '/tools', label: 'EXPORTS & TOOLS', desc: 'Download UMA files, injectors, and backups.' },
        { path: '/comms', label: 'HANGAR BAY', desc: 'Arrivals, departures, ship storage, and public accessible comms panel' },
    ];

    const iconClass = "text-white opacity-80";

    return (
        <PayPalScriptProvider options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "YOUR_PAYPAL_CLIENT_ID" }}>
            <div className="terminal glitchy min-h-screen p-4 font-mono text-green-300 center">
                <h2 className="glitch-text text-center text-green-400 text-xl mb-4">
                    <span className="inline-flex items-center gap-1">
                        <SatelliteDish size={20} className={iconClass} />
                        ORIGIN
                        <ArrowLeftRight size={20} className={iconClass} />
                        TERRA
                        <Satellite size={20} className={iconClass} />
                        07_A5 NODE -//UPLINK/2.2/
                    </span>
                </h2>

                {/* Status and Slideshow side-by-side */}
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch justify-center gap-5 mb-5">
                    {/* Left: VoucherStatus */}
                    <div className="flex flex-col justify-between flex-1 min-w-[320px] max-w-[446px] mx-auto p-4">
                        {error ? (
                            <p className="text-red-400">Error loading voucher data. Check server logs.</p>
                        ) : voucherData ? (
                            <VoucherStatus
                                username={voucherData.username}
                                userId={voucherData.id}
                                signup_date={voucherData.signup_date}
                                last_login={voucherData.last_login}
                                expiration={voucherData.uma_expiration}
                                autoRenew={voucherData.auto_renew}
                                statusCode={voucherData.status_code}
                                voucherType={voucherData.voucher_type}
                                file_health={voucherData.file_health}
                                user_counts={voucherData.user_counts}
                                system_counts={voucherData.system_counts}
                                onShowFuelModal={() => setShowFuelModal(true)}
                            />
                        ) : (
                            <p className="text-sm text-gray-400">Loading voucher status...</p>
                        )}
                        {/* Help & Account buttons side by side */}
                        <div className="flex gap-2 mt-4">
                            <button
                                className="btn-primary flex-1"
                                onClick={() => setShowHelpModal(true)}
                            >
                                Get Help
                            </button>
                            <button
                                className="btn-secondary flex-1"
                                onClick={() => setShowAccountModal(true)}
                            >
                                Account
                            </button>
                        </div>
                    </div>

                    {/* Right: Slideshow, Get Help button, News */}
                    <div className="flex flex-col items-center flex-1 min-w-[320px] p-4 md:p-4 justify-between">
                        {/* Slideshow */}
                        <Slideshow items={slideshowItems} />
                        {/* UMA + AI News */}
                        <div className="mt-2 w-full bg-black bg-opacity-70 border border-green-700 rounded p-2 text-green-200 text-xs font-mono text-center">
                            <span className="font-bold text-green-400">UMA + AI Industry News:</span>
                            <div className="mt-1 animate-pulse">
                                {/* Placeholder for news headlines, to be replaced with real RSS fetching */}
                                <div>OpenAI o3 pro is out and o3 price drops by 80%.</div>
                                <div>OpenAi releeased o4-mini-high and seems quite capable.</div>
                                <div>OpenAI relesaed GPT 4.1 (API only) big context and supreme CODEKING!.</div>
                                <div>The UMA early release some non-core functionality is being added.</div>
                            </div>
                        </div>
                    </div>
                </div>

                <table className="w-full text-sm border-collapse mt-2">
                    <thead>
                        <tr className="border-b border-green-600 text-green-300 font-bold text-base uppercase tracking-wide">
                            <th className="text-left pl-4 py-2 w-[30%]">Subsystem</th>
                            <th className="text-left pr-1 py-1">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subsystems.map((s, index) => (
                            <tr
                                key={index}
                                onClick={() => navigate(s.path)}
                                className="border-b border-green-800 hover:bg-green-900/20 cursor-pointer"
                            >
                                <td className="pl-4 py-2 text-white w-[30%]">
                                    {String(index + 1).padStart(2, '0')}. {s.label}
                                </td>
                                <td className="pr-1 py-1 text-green-400">{s.desc}</td>
                            </tr>
                        ))}

                        {/* Expandable timeline group */}
                        <tr onClick={() => setShowTimelines(!showTimelines)} className="cursor-pointer border-b border-green-800 hover:bg-green-900/20">
                            <td className="pl-4 py-2 text-white">
                                {showTimelines ? '0▾' : '0▸'} TOPIC TIMELINES
                            </td>
                            <td className="pr-4 py-2 text-green-400">View topic summaries, documents, and related chat significance scores</td>
                        </tr>

                        {showTimelines && topics.map((topic) => (
                            <tr
                                key={topic.topic_id}
                                onClick={() => navigate(`/topics?timeline=${topic.topic_id}`)}
                                className="border-b border-green-800 hover:bg-green-900/20 cursor-pointer"
                            >
                                <td className="pl-10 py-1 text-white">└── T{topic.topic_id}: {topic.topic_name}</td>
                                <td className="italic pr-4 py-1 text-green-400">Open the {topic.topic_name} topic timeline</td>
                            </tr>
                        ))}

                        {/* 10 Station Info (was 09 COMMS) */}
                        <tr onClick={() => setShowCommsModal(true)} className="cursor-pointer border-b border-green-800 hover:bg-green-900/20">
                            <td className="pl-4 py-2 text-white font-bold">10 STATION INFO</td>
                            <td className="pr-4 py-2 text-green-400">Access the FAQ and station communications</td>
                        </tr>

                        {/* 11 Legal (ISA-JAO) */}
                        <tr onClick={() => setShowISAJAOModal(true)} className="cursor-pointer border-b border-green-800 hover:bg-green-900/20">
                            <td className="pl-4 py-2 text-white font-bold">10 LEGAL (ISA-JAO)</td>
                            <td className="pr-4 py-2 text-green-400">IKOBU STATION is required to link the Independent Sectors Alliance - Judge Advocates Office datafeed to all active terminals.</td>
                        </tr>

                        <tr onClick={() => setShowSecurity(!showSecurity)} className="cursor-pointer border-b border-green-800 hover:bg-green-900/20">
                            <td className="pl-4 py-2 text-white glitch-text">
                                {showSecurity ? '0▾' : '0▸'} SECURITY
                            </td>
                            <td className="pr-4 py-2 text-green-400">Access security operations and services</td>
                        </tr>

                        <tr>
                            <td colSpan={2} style={{ padding: 0 }}>
                                <div className={`dropdown-content ${showSecurity ? 'open' : ''}`}>
                                    {showSecurity && (
                                        <>
                                            <div
                                                className="pl-10 py-2 text-white hover:bg-green-900/20 cursor-pointer"
                                                onClick={() => navigate('/security-terminal')}
                                            >
                                                └── Sec-Ops: Terminal
                                            </div>
                                            <div
                                                className="pl-10 py-2 text-white hover:bg-green-900/20 cursor-pointer"
                                                onClick={() => setShowDetectiveLink(true)}
                                            >
                                                └── DetectiveLINK
                                            </div>
                                            <div
                                                className="pl-10 py-2 text-white hover:bg-green-900/20 cursor-pointer"
                                                onClick={() => setShowSDSS(true)}
                                            >
                                                └── SDSS Recruitment Service
                                            </div>
                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>

                        {/* Add Fuel to the Station menu item */}
                        <tr onClick={() => setShowFuelModal(true)} className="cursor-pointer border-b border-green-800 hover:bg-green-900/20">
                            <td className="pl-4 py-2 text-yellow-300 font-bold">!DONATE POWERCELLS OR CREDITS NOW!</td>
                            <td className="pr-4 py-2 text-green-400 glitch-text">HELP CHARGE THE STATION! & move your UMA to a Premium Cluster in the Core!</td>
                        </tr>

                        <tr>
                            <td colSpan={2}>
                                <div className="border-t border-green-700 my-2 opacity-60"></div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* DetectiveLINK Popup */}
                {showDetectiveLink && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowDetectiveLink(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <p>Your system does not have the necessary credentials to access Active Station Case Files, please contact Security Terminal Services.</p>
                        </div>
                    </div>
                )}
                {/* SDSS Recruitment Service Popup */}
                {showSDSS && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowSDSS(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <p>
                                The Science Division Security Services is not recruiting at this time. Stay tuned to the IKOBU transmission pulse for particle activity alerts and contact the office. Earn Real Credits with the SSDS.<br /><br />
                                <span className="text-xs opacity-80">
                                    *SDSS provides Particle handling certification, Toxicicity reduction at the Station Learning Centre every 47 Cycles! Enroll at the SLC on the SEKTOR|RED Upper Deck.
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                {/* Fuel Modal */}
                {showFuelModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => { setShowFuelModal(false); setSelectedAmount(null); }} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <h3 className="text-lg font-bold mb-2 text-green-300">Fuel the Station</h3>
                            {!selectedAmount ? (
                                <div className="flex flex-col gap-3 mb-4">
                                    <button className="btn-primary" onClick={() => setSelectedAmount(1)}>$1 — Recharge +5%</button>
                                    <button className="btn-primary" onClick={() => setSelectedAmount(5)}>$5 — Recharge +30%</button>
                                    <button className="btn-primary" onClick={() => setSelectedAmount(10)}>$10 — Recharge +75%</button>
                                    <button className="btn-primary" onClick={() => setSelectedAmount(25)}>$25 — Recharge +200%</button>
                                </div>
                            ) : (
                                <div className="mb-4">
                                    <PayPalButtons
                                        style={{ layout: "vertical" }}
                                        forceReRender={[selectedAmount]}
                                        createOrder={(data, actions) => {
                                            return actions.order.create({
                                                purchase_units: [{ amount: { value: String(selectedAmount) } }]
                                            });
                                        }}
                                        onApprove={async (data, actions) => {
                                            await actions.order.capture();
                                            const token = localStorage.getItem('uma_token');
                                            await api.post('/users/donate', { amount: selectedAmount }, {
                                                headers: { Authorization: `Bearer ${token}` }
                                            });
                                            setShowFuelModal(false);
                                            setSelectedAmount(null);
                                            setShowSuccessModal(true);
                                        }}
                                        onCancel={() => setSelectedAmount(null)}
                                    />
                                    <button className="btn-secondary mt-2 w-full" onClick={() => setSelectedAmount(null)}>Back</button>
                                </div>
                            )}
                            <div className="text-green-200 text-xs mt-2">
                                IKOBU STATION is committed to serving umaFILES free for as long as the station has power. umaFILES are protected and do not lose data, however, as the station has been the home of the UMA for over 370 years, much of the data connections are slow. By donating powercells or credits to IKOBU STATION - NODE 07-A5, we can relocate your file to a more reliable section of the data core. If your umaFILE health downgrades again and you experience slow speed, the station will always need powercells.<br /><br />
                                <span className="text-yellow-300 font-bold">NOTE: TERAN VISITORS!</span> For a limited time while the banking consortium 44 is operational on the promenade outside SEKTOR|RED, we can process Earth currency!
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Modal */}
                {showSuccessModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowSuccessModal(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <h3 className="text-lg font-bold mb-2 text-green-300">umaFILE Relocated!</h3>
                            <p className="mb-4 text-green-200">
                                Your umaFILE has been relocated to an upgraded data core. File health will reconnect on next log in!
                            </p>
                            <div className="flex gap-4">
                                <button className="btn-primary" onClick={() => setShowSuccessModal(false)}>Close</button>
                                <button
                                    className="btn-secondary"
                                    onClick={() => {
                                        localStorage.removeItem('uma_token');
                                        window.location.href = '/umaf/';
                                    }}
                                >
                                    Log Out
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 10 Station Info Modal (FAQ) */}
                {showCommsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowCommsModal(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <h3 className="text-lg font-bold mb-2 text-blue-300">10 STATION INFO — FAQ</h3>
                            <div className="text-green-200 text-base mb-4 font-bold">Basic Station Knowledge. For detailed requests, use the Comms board in Hangar 9.</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ul className="list-disc pl-6 text-green-200 space-y-2">
                                    <li><strong>Q1:</strong> How do I recharge my UMA file?<br/><span className="text-green-400">A1: Use the 'Donate Powercells & Credits NOW!' option to instantly boost your file health.</span></li>
                                    <li><strong>Q2:</strong> What happens if my file health drops to zero?<br/><span className="text-green-400">A2: Your UMA file will be relocated to a slower data core, but your data is always protected.</span></li>
                                    <li><strong>Q3:</strong> Can I restore my file health without donating?<br/><span className="text-green-400">A3: File health will slowly recover over time, but donations provide instant upgrades.</span></li>
                                    <li><strong>Q4:</strong> How do I contact station support?<br/><span className="text-green-400">A4: Use the comms terminal or email support@ikobu.station.</span></li>
                                    <li><strong>Q5:</strong> Is my data private?<br/><span className="text-green-400">A5: Yes, all UMA files are encrypted and protected by station protocols.</span></li>
                                </ul>
                                <ul className="list-disc pl-6 text-green-200 space-y-2">
                                    <li><strong>Q6:</strong> Can I export my UMA file?<br/><span className="text-green-400">A6: Yes, use the 'EXPORTS & TOOLS' subsystem to download your data.</span></li>
                                    <li><strong>Q7:</strong> What is the station's uptime guarantee?<br/><span className="text-green-400">A7: IKOBU Station aims for 99.9% uptime, barring cosmic events.</span></li>
                                    <li><strong>Q8:</strong> How do I reset my password?<br/><span className="text-green-400">A8: Use the profile subsystem to update your credentials.</span></li>
                                    <li><strong>Q9:</strong> Can I invite others to the station?<br/><span className="text-green-400">A9: Yes, share your referral code from your profile page.</span></li>
                                    <li><strong>Q10:</strong> What are powercells?<br/><span className="text-green-400">A10: Powercells are digital credits used to maintain and upgrade UMA files.</span></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
                {/* ISAJAO Modal */}
                {showISAJAOModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowISAJAOModal(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <h3 className="text-lg font-bold mb-2 text-pink-300">ISAJAO — Terms & Conditions</h3>
                            <div className="text-green-200 text-sm space-y-2">
                                <p><strong>1. Acceptance of Terms:</strong> By accessing IKOBU Station, you agree to abide by all station protocols and interstellar regulations.</p>
                                <p><strong>2. Data Security:</strong> All UMA files are encrypted and protected. Unauthorized access is strictly prohibited.</p>
                                <p><strong>3. Donations:</strong> Powercell and credit donations are voluntary and non-refundable. All proceeds support station operations.</p>
                                <p><strong>4. User Conduct:</strong> Users must not engage in malicious activity or disrupt station services.</p>
                                <p><strong>5. Liability:</strong> IKOBU Station is not responsible for cosmic events or data loss due to interstellar anomalies.</p>
                                <p><strong>6. Updates:</strong> Terms may be updated at any time. Continued use constitutes acceptance of changes.</p>
                                <p><strong>7. Contact:</strong> For questions, contact the Independent Space Alliance Judge Advocates Office (Section 8675).</p>
                            </div>
                        </div>
                    </div>
                )}

                {showAccountModal && (
                    <AccountModal onClose={() => setShowAccountModal(false)} />
                )}

                {showHelpModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowHelpModal(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <h3 className="text-lg font-bold mb-2 text-green-300">IKOBU SUPPORT</h3>
                            <div className="text-green-200 text-base mb-4 font-bold">
                                TO REACH THE IKOBU SUPPORT TEAM, please submit your request in the upper right terminal box and select support.<br /><br />We hope you enjoy your stay on IKOBU STATION — <span className="text-green-400">The gateway to the UMA!</span>
                            </div>
                            <button
                                className="btn-primary w-full mt-2"
                                onClick={() => { setShowHelpModal(false); navigate('/comms'); }}
                            >
                                Go to Comms Page
                            </button>
                        </div>
                    </div>
                )}

                <footer className="mt-10 text-xs text-green-400 font-mono opacity-80 text-center leading-tight">
                    IKOBU STATION 07-A5 — Gateway to the UMA<br />
                    "Connecting organic and mechanic for over 370 years"
                </footer>
            </div>
        </PayPalScriptProvider>
    );
};

export default Dashboard;